import { Button, Tag } from "@design";
import { useInfiniteQuery } from "@tanstack/react-query";
import { FileText, Image } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AttachmentResponse,
  listMessageAttachmentsInfiniteOptions,
  type SrcSchemasChatMessageResponse,
} from "@/api";
import { formatNumber } from "@/i18n/format";

/** Tags shown before "Show more": a message with hundreds of files stays a short row. */
export const COLLAPSED_ATTACHMENTS = 8;
/** Attachments per message in the chat history response (`attachmentsLimit`); the rest page in on request. */
export const HISTORY_ATTACHMENTS_PAGE = 24;
const PAGE = 50;
const NOT_READY = new Set(["uploading", "pending_start", "queued", "preparing"]);
const ENDED = new Set(["failed", "rejected", "cancelled"]);

/** A file sent with the message being answered, shown before the message is saved (MHS-1007). */
export interface SentAttachment {
  key: string;
  name: string;
  mimeType: string;
}

function FileTag({ name, mimeType, note }: Readonly<{ name: string; mimeType: string; note?: string | null }>) {
  const { t } = useTranslation("chat", { keyPrefix: "attachments" });
  return (
    <Tag icon={mimeType.startsWith("image/") ? <Image size={12} /> : <FileText size={12} />}>
      {note ? t("withState", { name, state: note }) : name}
    </Tag>
  );
}

function AttachmentTag({ attachment }: Readonly<{ attachment: AttachmentResponse }>) {
  const { t } = useTranslation("chat", { keyPrefix: "attachments" });
  const state = attachment.lifecycle ?? "";
  const ended = ENDED.has(state) ? t("state.unavailable") : null;
  const note = NOT_READY.has(state) ? t("state.preparing") : ended;
  return <FileTag name={attachment.filename} mimeType={attachment.mimeType} note={note} />;
}

/** The files of a message still being answered: its saved copy (with attachments) shows once the reply ends. */
export function SentAttachments({ files }: Readonly<{ files: SentAttachment[] }>) {
  const { t } = useTranslation("chat", { keyPrefix: "attachments" });
  const [expanded, setExpanded] = useState(false);
  if (files.length === 0) return null;
  const visible = expanded ? files : files.slice(0, COLLAPSED_ATTACHMENTS);
  const hidden = files.length - visible.length;
  return (
    <div className="flex max-w-[80%] flex-col items-end gap-1.5">
      <div className="flex flex-wrap justify-end gap-1.5">
        {visible.map((f) => (
          <FileTag key={f.key} name={f.name} mimeType={f.mimeType} />
        ))}
      </div>
      {hidden > 0 || expanded ? (
        <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
          {expanded ? t("showLess") : t("showMore", { count: hidden, shown: formatNumber(hidden) })}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Files sent with a message (MHS-646): collapsed to a few tags with a count. History returns only a first page
 * per message; "Show more" pages the rest in from the server, so long histories stay bounded.
 */
export function MessageAttachments({ message }: Readonly<{ message: SrcSchemasChatMessageResponse }>) {
  const { t } = useTranslation("chat", { keyPrefix: "attachments" });
  const [expanded, setExpanded] = useState(false);
  const first = message.attachments ?? [];
  const cursor = message.attachmentsNextCursor ?? null;
  const more = useInfiniteQuery({
    ...listMessageAttachmentsInfiniteOptions({ path: { message_uuid: message.uuid }, query: { limit: PAGE } }),
    initialPageParam: cursor,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: expanded && cursor !== null,
  });
  const loaded = [...first, ...(more.data?.pages.flatMap((p) => p.data) ?? [])];
  if (loaded.length === 0) return null;

  const total = cursor ? Math.max(message.attachmentCount ?? 0, loaded.length) : loaded.length;
  const visible = expanded ? loaded : loaded.slice(0, COLLAPSED_ATTACHMENTS);
  const hidden = total - visible.length;
  const showMore = () => {
    if (!expanded) setExpanded(true);
    else if (more.hasNextPage) void more.fetchNextPage();
  };

  return (
    <div className="flex max-w-[80%] flex-col items-end gap-1.5">
      <div className="flex flex-wrap justify-end gap-1.5">
        {visible.map((a) => (
          <AttachmentTag key={a.uuid} attachment={a} />
        ))}
      </div>
      {hidden > 0 || expanded ? (
        <div className="flex gap-2">
          {hidden > 0 ? (
            <Button variant="ghost" size="sm" onClick={showMore} loading={more.isFetching}>
              {t("showMore", { count: hidden, shown: formatNumber(hidden) })}
            </Button>
          ) : null}
          {expanded ? (
            <Button variant="ghost" size="sm" onClick={() => setExpanded(false)}>
              {t("showLess")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
